import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { API } from "@infra/implementations/api";
import { useImpersonationContext } from "../../hooks/ImpersonationContext";
import type { ImpersonatableUser } from "@infra/implementations/api/routers/users";

export function useImpersonationSelector(companyId?: string, enabled = true) {
	const { impersonatedEmail, isImpersonating, startImpersonation, stopImpersonation } = useImpersonationContext();
	const [allUsers, setAllUsers] = useState<ImpersonatableUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const hasFetched = useRef(false);

	const fetchUsers = useCallback(async () => {
		if (hasFetched.current) return;
		setIsLoading(true);
		try {
			const result = await API.Users.getImpersonatableUsers();
			setAllUsers(result);
			hasFetched.current = true;
		} catch {
			// Error handled silently; dropdown will show empty state
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Pre-fetch on mount (only for eligible users)
	useEffect(() => {
		if (enabled) fetchUsers();
	}, [enabled, fetchUsers]);

	const users = useMemo(() => {
		if (!companyId) return allUsers;
		return allUsers.filter((u) => u.companyIds.includes(companyId));
	}, [allUsers, companyId]);

	return { impersonatedEmail, isImpersonating, startImpersonation, stopImpersonation, users, isLoading, fetchUsers };
}
